import React, { forwardRef, useEffect, useRef, useState } from 'react';

import Cross from '@/assets/cross.svg?react';
import cn from '@/utils/cn';

import ErrorMessage from './ErrorMessage';
import { MultiTextInputProps } from './Input.types';
import { useLingui } from '@lingui/react';

const MultiTextInput = forwardRef<HTMLInputElement, MultiTextInputProps>(
  (
    {
      className,
      error,
      success,
      disabled,
      value = [],
      type,
      onChange,
      ...props
    },
    ref
  ) => {
    const [record, setRecord] = useState('');

    const [focus, setFocus] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setFocus(false);
        }
      }

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [containerRef]);

    const { i18n } = useLingui();

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !!record) {
        event.preventDefault();
        if (record === ' ') return;
        if (!value.map((i) => i.toLowerCase()).includes(record.toLowerCase()))
          // onChange((prevArray) => [...prevArray, record]);
          onChange([...value, record]);
        setRecord('');
      }
      if (event.key === 'Backspace' && record === '' && value.length > 0) {
        removeItem(value.length - 1);
      }
    };

    const removeItem = (indexToRemove: number) => {
      onChange(value.filter((_, index) => index !== indexToRemove));
    };

    return (
      <div className="rounded-md bg-white">
        <div
          ref={containerRef}
          className={cn(
            'flex h-auto flex-nowrap items-center justify-between rounded-md py-2.5 pl-3.5 outline outline-1 outline-dark-gray-100',
            focus && 'outline-blue-600 ring-4 ring-primary/20',
            success && 'outline-green-400',
            !!error && 'outline-red-400',
            disabled && 'bg-gray-200 outline-dark-gray-100'
          )}
        >
          <div className="flex w-full flex-wrap items-center gap-1.5">
            {value.map((item, idx) => {
              return (
                <div
                  onClick={(event) => event.stopPropagation()}
                  className="flex w-fit cursor-default items-stretch gap-2 rounded bg-gray-200 text-left"
                  key={item}
                >
                  <p className="caption-large my-1.5 ml-2.5"> {item}</p>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Remove item"
                    onClick={() => removeItem(idx)}
                    className="flex cursor-pointer items-center rounded px-1 hover:bg-gray-500"
                  >
                    <Cross width={14} className="text-dark-gray-500" />
                  </span>
                </div>
              );
            })}
            <input
              value={record}
              onChange={(e) =>
                setRecord(
                  e.target.value.length > 50
                    ? e.target.value.slice(0, 50)
                    : e.target.value
                )
              }
              type={type}
              className={cn(
                'body h-auto grow leading-8 outline-none placeholder:text-dark-gray-200 disabled:bg-gray-200 disabled:placeholder:text-dark-gray-300',
                className
              )}
              placeholder={
                value.length === 0 ? i18n.t('Napisz coś i wciśnij enter') : ''
              }
              disabled={disabled}
              onFocus={() => setFocus(true)}
              ref={ref}
              onKeyDown={handleKeyDown}
              data-testid={`${props.name}-input`}
              {...props}
              onBlur={() => {
                if (
                  record &&
                  record.length > 2 &&
                  !value
                    .map((i) => i.toLowerCase())
                    .includes(record.toLowerCase())
                ) {
                  onChange([...value, record]);
                  setRecord('');
                }
              }}
            />
          </div>
          <Cross
            className={cn(
              'mx-3.5 my-2 min-h-[14px] min-w-[14px] cursor-pointer text-dark-gray-400 transition-colors hover:text-dark-gray-300',
              value.length === 0 && 'hidden'
            )}
            width={14}
            height={14}
            onClick={() => {
              onChange([]);
            }}
          />
        </div>
        {error && !success && <ErrorMessage errorMessage={error} />}
      </div>
    );
  }
);

MultiTextInput.displayName = 'MultiTextInput';

export default MultiTextInput;
